import React, { useState, useEffect, useCallback } from 'react';
import {
  RefreshCw,
  Plus,
  Trash2,
  Calendar,
  Archive,
  AlertTriangle,
  Clock,
  CheckCircle2,
  X,
  Search,
  Database,
  Timer,
  FileText,
  ShieldAlert,
} from 'lucide-react';
import PageInfoButton from '@/components/PageInfoButton';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type ScheduleStatus = 'active' | 'due_for_review' | 'due_for_deletion' | 'archived';
type DeletionMethod = 'secure_erasure' | 'anonymisation' | 'pseudonymisation' | 'physical_destruction' | 'crypto_shredding';
type RetentionUnit = 'days' | 'months' | 'years';
type TriggerType = 'collection_date' | 'contract_end' | 'consent_withdrawal' | 'last_activity' | 'custom';

interface RetentionSchedule {
  id: number;
  data_category: string;
  processing_activity: string | null;
  retention_period_value: number;
  retention_period_unit: RetentionUnit;
  trigger: TriggerType;
  deletion_method: DeletionMethod;
  auto_delete: boolean;
  next_review_date: string | null;
  status: ScheduleStatus;
  notes: string | null;
  created_at: string;
}

interface DeletionLog {
  id: number;
  deletion_date: string;
  data_category: string;
  records_description: string;
  record_count: number;
  deletion_method: DeletionMethod;
  deleted_by_name: string | null;
  approved_by_name: string | null;
  notes: string | null;
}

interface DashboardStats {
  active_schedules: number;
  due_for_review: number;
  due_for_deletion: number;
  recent_deletions: number;
}

interface ScheduleForm {
  data_category: string;
  processing_activity: string;
  retention_period_value: number;
  retention_period_unit: RetentionUnit;
  trigger: TriggerType;
  deletion_method: DeletionMethod;
  auto_delete: boolean;
  next_review_date: string;
  notes: string;
}

interface DeletionForm {
  data_category: string;
  records_description: string;
  record_count: number;
  deletion_method: DeletionMethod;
  notes: string;
}

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const STATUS_BADGE: Record<ScheduleStatus, string> = {
  active: 'bg-green-100 text-green-800',
  due_for_review: 'bg-amber-100 text-amber-800',
  due_for_deletion: 'bg-red-100 text-red-800',
  archived: 'bg-slate-100 text-slate-600',
};

const DELETION_METHODS: DeletionMethod[] = ['secure_erasure', 'anonymisation', 'pseudonymisation', 'physical_destruction', 'crypto_shredding'];
const TRIGGERS: TriggerType[] = ['collection_date', 'contract_end', 'consent_withdrawal', 'last_activity', 'custom'];
const UNITS: RetentionUnit[] = ['days', 'months', 'years'];
const SCHEDULE_STATUSES: ScheduleStatus[] = ['active', 'due_for_review', 'due_for_deletion', 'archived'];

const METHOD_BADGE: Record<DeletionMethod, string> = {
  secure_erasure: 'bg-red-50 text-red-700',
  anonymisation: 'bg-blue-50 text-blue-700',
  pseudonymisation: 'bg-indigo-50 text-indigo-700',
  physical_destruction: 'bg-orange-50 text-orange-700',
  crypto_shredding: 'bg-purple-50 text-purple-700',
};

const emptyScheduleForm: ScheduleForm = {
  data_category: '',
  processing_activity: '',
  retention_period_value: 1,
  retention_period_unit: 'years',
  trigger: 'collection_date',
  deletion_method: 'secure_erasure',
  auto_delete: false,
  next_review_date: '',
  notes: '',
};

const emptyDeletionForm: DeletionForm = {
  data_category: '',
  records_description: '',
  record_count: 0,
  deletion_method: 'secure_erasure',
  notes: '',
};

const fmtLabel = (s: string) => s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
const fmtDate = (d: string | null) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '--';

const isDueSoon = (d: string | null) => {
  if (!d) return false;
  const diff = new Date(d).getTime() - Date.now();
  return diff > 0 && diff < 30 * 24 * 60 * 60 * 1000; // 30 days
};
const isOverdue = (d: string | null) => d ? new Date(d).getTime() < Date.now() : false;

/* ------------------------------------------------------------------ */
/*  API helpers                                                        */
/* ------------------------------------------------------------------ */

const getCsrfToken = () => document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
  'X-CSRF-Token': getCsrfToken(),
});

const API = '/api/compliance/data-retention';

async function apiFetch<T>(url: string, opts?: RequestInit): Promise<T> {
  const res = await fetch(url, { headers: authHeaders(), ...opts });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || body.error || `Request failed (${res.status})`);
  }
  return res.json();
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const DataRetention: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'schedules' | 'deletions'>('schedules');
  const [schedules, setSchedules] = useState<RetentionSchedule[]>([]);
  const [deletions, setDeletions] = useState<DeletionLog[]>([]);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Schedule modal
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleForm, setScheduleForm] = useState<ScheduleForm>({ ...emptyScheduleForm });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  // Deletion modal
  const [showDeletionModal, setShowDeletionModal] = useState(false);
  const [deletionForm, setDeletionForm] = useState<DeletionForm>({ ...emptyDeletionForm });

  /* ---------- Load ---------- */

  const load = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (searchTerm) params.set('search', searchTerm);
      const qs = params.toString() ? `?${params}` : '';

      const [schedulesRes, deletionsRes, statsRes] = await Promise.all([
        apiFetch<any>(`${API}${qs}`),
        apiFetch<any>(`${API}/deletion-log`),
        apiFetch<DashboardStats>(`${API}/dashboard`),
      ]);

      setSchedules(Array.isArray(schedulesRes) ? schedulesRes : schedulesRes.schedules ?? schedulesRes.data ?? []);
      setDeletions(Array.isArray(deletionsRes) ? deletionsRes : deletionsRes.deletions ?? deletionsRes.data ?? []);
      setStats(statsRes);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter, searchTerm]);

  useEffect(() => { load(); }, [load]);

  /* ---------- Create Schedule ---------- */

  const handleCreateSchedule = async () => {
    if (!scheduleForm.data_category.trim()) { setFormError('Data category is required.'); return; }
    setSubmitting(true);
    setFormError('');
    try {
      await apiFetch(API, {
        method: 'POST',
        body: JSON.stringify({
          ...scheduleForm,
          next_review_date: scheduleForm.next_review_date || null,
          notes: scheduleForm.notes || null,
        }),
      });
      setShowScheduleModal(false);
      setScheduleForm({ ...emptyScheduleForm });
      load();
    } catch (e: any) {
      setFormError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  /* ---------- Log Deletion ---------- */

  const handleLogDeletion = async () => {
    if (!deletionForm.data_category.trim() || !deletionForm.records_description.trim()) {
      setFormError('Data category and records description are required.');
      return;
    }
    setSubmitting(true);
    setFormError('');
    try {
      await apiFetch(`${API}/deletion-log`, {
        method: 'POST',
        body: JSON.stringify(deletionForm),
      });
      setShowDeletionModal(false);
      setDeletionForm({ ...emptyDeletionForm });
      load();
    } catch (e: any) {
      setFormError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  /* ---------- Render ---------- */

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-sky-100 rounded-xl">
            <Archive size={24} className="text-sky-600" />
          </div>
          <div>
            <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900">Data Retention</h1><PageInfoButton /></div>
            <p className="text-sm text-slate-500">Manage retention schedules and deletion logs</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="flex items-center gap-1 px-3 py-2 text-sm text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50 transition">
            <RefreshCw size={16} /> Refresh
          </button>
          {activeTab === 'schedules' ? (
            <button onClick={() => { setShowScheduleModal(true); setFormError(''); }} className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-white bg-sky-600 rounded-lg hover:bg-sky-700 transition shadow-sm">
              <Plus size={16} /> New Schedule
            </button>
          ) : (
            <button onClick={() => { setShowDeletionModal(true); setFormError(''); }} className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-white bg-sky-600 rounded-lg hover:bg-sky-700 transition shadow-sm">
              <Plus size={16} /> Log Deletion
            </button>
          )}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm flex items-center gap-2">
          <AlertTriangle size={16} /> {error}
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Active Schedules', value: stats.active_schedules, icon: Database, color: 'text-sky-700', bg: 'bg-sky-50' },
            { label: 'Due for Review', value: stats.due_for_review, icon: Clock, color: 'text-amber-700', bg: 'bg-amber-50' },
            { label: 'Due for Deletion', value: stats.due_for_deletion, icon: ShieldAlert, color: 'text-red-700', bg: 'bg-red-50' },
            { label: 'Recent Deletions', value: stats.recent_deletions, icon: Trash2, color: 'text-slate-700', bg: 'bg-slate-50' },
          ].map(({ label, value, icon: Icon, color, bg }) => (
            <div key={label} className={`${bg} border border-slate-200 rounded-xl p-4`}>
              <div className="flex items-center gap-2 mb-1">
                <Icon size={16} className={color} />
                <p className="text-sm text-slate-500">{label}</p>
              </div>
              <p className={`text-2xl font-bold ${color}`}>{value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit">
        {(['schedules', 'deletions'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium rounded-md transition ${
              activeTab === tab ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {tab === 'schedules' ? 'Retention Schedules' : 'Deletion Log'}
          </button>
        ))}
      </div>

      {/* Filters */}
      {activeTab === 'schedules' && (
        <div className="flex flex-wrap items-center gap-3 bg-white border border-slate-200 rounded-xl p-4">
          <Search size={16} className="text-slate-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search by category..."
            className="border border-slate-300 rounded-lg px-3 py-2 text-sm flex-1 min-w-[180px] focus:ring-2 focus:ring-sky-500 focus:border-sky-500 outline-none"
          />
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
            <option value="">All Statuses</option>
            {SCHEDULE_STATUSES.map(s => <option key={s} value={s}>{fmtLabel(s)}</option>)}
          </select>
          {(statusFilter || searchTerm) && (
            <button onClick={() => { setStatusFilter(''); setSearchTerm(''); }} className="text-xs text-slate-500 hover:text-slate-700 underline">
              Clear filters
            </button>
          )}
        </div>
      )}

      {/* Loading */}
      {isLoading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-600" />
        </div>
      ) : activeTab === 'schedules' ? (
        /* Schedules Table */
        schedules.length === 0 ? (
          <div className="py-16 bg-white border border-slate-200 rounded-xl">
            <Archive size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500 text-lg font-medium">No retention schedules found</p>
            <p className="text-slate-400 text-sm mt-1">Create a retention schedule to get started.</p>
            <button onClick={() => setShowScheduleModal(true)} className="mt-4 inline-flex items-center gap-1 px-4 py-2 text-sm text-white bg-sky-600 rounded-lg hover:bg-sky-700">
              <Plus size={16} /> New Schedule
            </button>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr>
                    {['Data Category', 'Retention Period', 'Trigger', 'Deletion Method', 'Next Review', 'Status'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {schedules.map(s => {
                    const reviewHighlight = isOverdue(s.next_review_date) ? 'text-red-600 font-semibold' :
                      isDueSoon(s.next_review_date) ? 'text-amber-600 font-medium' : 'text-slate-600';
                    return (
                      <tr key={s.id} className="hover:bg-slate-50 transition">
                        <td className="px-4 py-3 text-left">
                          <div>
                            <p className="font-medium text-slate-900">{s.data_category}</p>
                            {s.processing_activity && <p className="text-xs text-slate-400 mt-0.5">{s.processing_activity}</p>}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-left">
                          <span className="inline-flex items-center gap-1 text-slate-700">
                            <Timer size={14} className="text-slate-400" />
                            {s.retention_period_value} {s.retention_period_unit}
                          </span>
                          {s.auto_delete && (
                            <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-50 text-red-600">AUTO</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-left">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                            {fmtLabel(s.trigger)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-left">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${METHOD_BADGE[s.deletion_method]}`}>
                            {fmtLabel(s.deletion_method)}
                          </span>
                        </td>
                        <td className={`px-4 py-3 text-xs ${reviewHighlight}`}>
                          {fmtDate(s.next_review_date)}
                          {isOverdue(s.next_review_date) && (
                            <span className="ml-1 inline-flex items-center"><AlertTriangle size={12} className="text-red-500" /></span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-left">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[s.status]}`}>
                            {fmtLabel(s.status)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )
      ) : (
        /* Deletion Log Table */
        deletions.length === 0 ? (
          <div className="py-16 bg-white border border-slate-200 rounded-xl">
            <Trash2 size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500 text-lg font-medium">No deletion records</p>
            <p className="text-slate-400 text-sm mt-1">Deletion events will appear here once logged.</p>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr>
                    {['Date', 'Data Category', 'Records Description', 'Count', 'Method', 'Deleted By', 'Approved By'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {deletions.map(d => (
                    <tr key={d.id} className="hover:bg-slate-50 transition">
                      <td className="px-4 py-3 text-left text-slate-600 text-xs whitespace-nowrap">{fmtDate(d.deletion_date)}</td>
                      <td className="px-4 py-3 text-left font-medium text-slate-900">{d.data_category}</td>
                      <td className="px-4 py-3 text-left text-slate-600 max-w-[200px] truncate">{d.records_description}</td>
                      <td className="px-4 py-3 text-left text-slate-700 font-mono">{(d.record_count ?? 0).toLocaleString()}</td>
                      <td className="px-4 py-3 text-left">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${METHOD_BADGE[d.deletion_method]}`}>
                          {fmtLabel(d.deletion_method)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-left text-slate-600">{d.deleted_by_name || '--'}</td>
                      <td className="px-4 py-3 text-left text-slate-600">
                        {d.approved_by_name ? (
                          <span className="inline-flex items-center gap-1">
                            <CheckCircle2 size={12} className="text-green-500" /> {d.approved_by_name}
                          </span>
                        ) : '--'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      )}

      {/* ---- Schedule Create Modal ---- */}
      {showScheduleModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h2 className="text-lg font-semibold text-slate-900">New Retention Schedule</h2>
              <button onClick={() => setShowScheduleModal(false)} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              {formError && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{formError}</div>}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Data Category *</label>
                <input
                  type="text"
                  value={scheduleForm.data_category}
                  onChange={e => setScheduleForm(f => ({ ...f, data_category: e.target.value }))}
                  placeholder="e.g. Customer Personal Data, Employee Records"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-sky-500 focus:border-sky-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Processing Activity</label>
                <input
                  type="text"
                  value={scheduleForm.processing_activity}
                  onChange={e => setScheduleForm(f => ({ ...f, processing_activity: e.target.value }))}
                  placeholder="e.g. Customer onboarding, Payroll processing"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-sky-500 focus:border-sky-500 outline-none"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Retention Value *</label>
                  <input
                    type="number"
                    min={1}
                    value={scheduleForm.retention_period_value}
                    onChange={e => setScheduleForm(f => ({ ...f, retention_period_value: parseInt(e.target.value) || 1 }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-sky-500 focus:border-sky-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Unit</label>
                  <select
                    value={scheduleForm.retention_period_unit}
                    onChange={e => setScheduleForm(f => ({ ...f, retention_period_unit: e.target.value as RetentionUnit }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-sky-500 focus:border-sky-500 outline-none"
                  >
                    {UNITS.map(u => <option key={u} value={u}>{fmtLabel(u)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Trigger</label>
                  <select
                    value={scheduleForm.trigger}
                    onChange={e => setScheduleForm(f => ({ ...f, trigger: e.target.value as TriggerType }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-sky-500 focus:border-sky-500 outline-none"
                  >
                    {TRIGGERS.map(t => <option key={t} value={t}>{fmtLabel(t)}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Deletion Method</label>
                  <select
                    value={scheduleForm.deletion_method}
                    onChange={e => setScheduleForm(f => ({ ...f, deletion_method: e.target.value as DeletionMethod }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-sky-500 focus:border-sky-500 outline-none"
                  >
                    {DELETION_METHODS.map(m => <option key={m} value={m}>{fmtLabel(m)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Next Review Date</label>
                  <input
                    type="date"
                    value={scheduleForm.next_review_date}
                    onChange={e => setScheduleForm(f => ({ ...f, next_review_date: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-sky-500 focus:border-sky-500 outline-none"
                  />
                </div>
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="auto_delete"
                  checked={scheduleForm.auto_delete}
                  onChange={e => setScheduleForm(f => ({ ...f, auto_delete: e.target.checked }))}
                  className="w-4 h-4 text-sky-600 border-slate-300 rounded focus:ring-sky-500"
                />
                <label htmlFor="auto_delete" className="text-sm text-slate-700">
                  Enable automatic deletion when retention period expires
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                <textarea
                  value={scheduleForm.notes}
                  onChange={e => setScheduleForm(f => ({ ...f, notes: e.target.value }))}
                  rows={2}
                  placeholder="Additional notes or legal basis..."
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-sky-500 focus:border-sky-500 outline-none resize-none"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-xl">
              <button onClick={() => setShowScheduleModal(false)} className="px-4 py-2 text-sm text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50">Cancel</button>
              <button onClick={handleCreateSchedule} disabled={submitting} className="px-5 py-2 text-sm font-medium text-white bg-sky-600 rounded-lg hover:bg-sky-700 disabled:opacity-50 shadow-sm">
                {submitting ? 'Creating...' : 'Create Schedule'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---- Deletion Log Modal ---- */}
      {showDeletionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h2 className="text-lg font-semibold text-slate-900">Log Data Deletion</h2>
              <button onClick={() => setShowDeletionModal(false)} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              {formError && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{formError}</div>}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Data Category *</label>
                <input
                  type="text"
                  value={deletionForm.data_category}
                  onChange={e => setDeletionForm(f => ({ ...f, data_category: e.target.value }))}
                  placeholder="e.g. Customer Personal Data"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-sky-500 focus:border-sky-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Records Description *</label>
                <textarea
                  value={deletionForm.records_description}
                  onChange={e => setDeletionForm(f => ({ ...f, records_description: e.target.value }))}
                  rows={2}
                  placeholder="Describe what data was deleted..."
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-sky-500 focus:border-sky-500 outline-none resize-none"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Record Count</label>
                  <input
                    type="number"
                    min={0}
                    value={deletionForm.record_count}
                    onChange={e => setDeletionForm(f => ({ ...f, record_count: parseInt(e.target.value) || 0 }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-sky-500 focus:border-sky-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Deletion Method</label>
                  <select
                    value={deletionForm.deletion_method}
                    onChange={e => setDeletionForm(f => ({ ...f, deletion_method: e.target.value as DeletionMethod }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-sky-500 focus:border-sky-500 outline-none"
                  >
                    {DELETION_METHODS.map(m => <option key={m} value={m}>{fmtLabel(m)}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                <textarea
                  value={deletionForm.notes}
                  onChange={e => setDeletionForm(f => ({ ...f, notes: e.target.value }))}
                  rows={2}
                  placeholder="Reason for deletion, authorisation reference..."
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-sky-500 focus:border-sky-500 outline-none resize-none"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-xl">
              <button onClick={() => setShowDeletionModal(false)} className="px-4 py-2 text-sm text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50">Cancel</button>
              <button onClick={handleLogDeletion} disabled={submitting} className="px-5 py-2 text-sm font-medium text-white bg-sky-600 rounded-lg hover:bg-sky-700 disabled:opacity-50 shadow-sm">
                {submitting ? 'Logging...' : 'Log Deletion'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataRetention;
